import React, { useEffect, useMemo, useState } from "react";
import { Trans } from "@lingui/macro";
import PropTypes from "prop-types";

import { Time, TIME_FORMAT_TYPES } from "widgets/inputs";
import { Icon, Collage } from "widgets/datadisplay";
import { Tooltip, TOOLTIP_TYPES, TOOLTIP_POSITION } from "widgets/feedback";
import { Classes } from "base/classes";
import {
  ReadonlyEditor,
  BoldPlugin,
  ItalicPlugin,
  LinkPlugin,
  UnderlinePlugin,
  StrikePlugin,
  CodePlugin,
  HeaderPlugin,
  ListPlugin,
  QuotePlugin,
  AlignPlugin,
  ColorPlugin,
  VideoPlugin,
  ImagePlugin,
  MentionPlugin,
  FORMAT_TYPES
} from "widgets/richtext";
import { useUserStore } from "base/stores";
import { generateId } from "base/utils";

import { Embedurl } from "../post/embedurl";
import { voteDetails, getVoteCount, isNoContent } from "@/common/utils/utils";
import { DOWNVOTE, UPVOTE } from "@/common/utils/constant";
import { ForumService } from "@/common/service/forum.service";
import { useForumStore } from "@/common/store/forum.store";
import { useChannelStore } from "@/common/store/channel.store";
import { CommentAttachments } from "./comment.attachments";

import styles from "../../forum/forum.css";

import UpvoteIcon from "@/common/icons/Upvote.svg";
import DownvoteIcon from "@/common/icons/Downvote.svg";
import UpvotedIcon from "@/common/icons/Upvoted.svg";
import DownvotedIcon from "@/common/icons/Downvoted.svg";

export function Comment({ comment, teamId, topicId }) {
  const [reaction, setReactions] = useState({});
  const [voteCount, setVoteCount] = useState(0);
  const { currentUser } = useUserStore.getState();
  const { topicData } = useForumStore((state) => ({
    topicData: state.topicData[topicId]
  }));
  const { mediumId } = useChannelStore.getState();
  const plugin = [
    BoldPlugin,
    ItalicPlugin,
    LinkPlugin,
    UnderlinePlugin,
    StrikePlugin,
    HeaderPlugin,
    CodePlugin,
    ListPlugin,
    QuotePlugin,
    AlignPlugin,
    ColorPlugin,
    VideoPlugin,
    ImagePlugin,
    MentionPlugin,
    {
      formatType: FORMAT_TYPES.EMBED_LINK,
      component: React.forwardRef((prop, ref) => {
        return <Embedurl innerRef={ref} {...prop} />;
      })
    },
    {
      formatType: FORMAT_TYPES.COLLAGE,
      component: React.forwardRef((prop, ref) => {
        return <Collage innerRef={ref} {...prop} />;
      })
    }
  ];

  const { isUpvoted, isDownvoted } = useMemo(
    function getVoteDetails() {
      return voteDetails(reaction);
    },
    [reaction]
  );

  // eslint-disable-next-line kf/hook-func-name
  const isDisableVote = useMemo(
    function checkVoteDisable() {
      return (
        comment?._created_by?._id === currentUser._id || topicData.IsClosed
      );
    },
    [comment?._created_by?._id, currentUser._id, topicData.IsClosed]
  );

  const { commentContent, attachments } = useMemo(
    function setValues() {
      let attachments = { images: [], files: [] };
      const commentContent = comment.Content.filter(
        (comment) =>
          ![FORMAT_TYPES.COLLAGE, FORMAT_TYPES.BLOCKQUOTE].includes(
            comment.type
          )
      );
      const attachment = comment.Content.find(
        (comment) => comment.type === FORMAT_TYPES.COLLAGE
      );
      if (attachment) {
        attachments = attachment.data;
      }
      return { commentContent, attachments };
    },
    [comment]
  );

  useEffect(
    function setDetails() {
      const reactions = comment?.Reactions?.find(
        (reaction) => reaction._id === currentUser._id
      );
      setReactions(reactions);
    },
    [comment, currentUser._id]
  );

  useEffect(
    function setCount() {
      setVoteCount(comment.VoteCount || 0);
    },
    [comment.VoteCount]
  );

  function postVote(voteType) {
    if (!isDisableVote) {
      const eventId = generateId("Ev");
      useForumStore.getState().setEventId(eventId);
      const data = {
        ReactionType: voteType,
        EventId: eventId
      };
      const commentVoteCount = getVoteCount(
        isUpvoted,
        isDownvoted,
        voteType,
        voteCount
      );
      if (
        (isUpvoted && voteType === UPVOTE) ||
        (isDownvoted && voteType === DOWNVOTE)
      ) {
        const [removeVotePromise] = ForumService.removeCommentVote(
          teamId,
          mediumId,
          topicData._id,
          comment._id
        );
        removeVotePromise().catch((error) => {
          const reactions = comment?.Reactions?.find(
            (reaction) => reaction._id === currentUser._id
          );
          setReactions(reactions);
        });
        if (comment._id === topicData.BestSolution) {
          useForumStore
            .getState()
            .setBestSolutionReactions(
              topicId,
              {},
              commentVoteCount,
              comment._id
            );
        } else {
          useForumStore
            .getState()
            .updateCommentVote(topicId, comment._id, {}, commentVoteCount);
        }
      } else {
        const [postVotePromise] = ForumService.postCommentVote(
          teamId,
          mediumId,
          topicData._id,
          comment._id,
          data
        );
        postVotePromise().catch(() => {
          const reactions = comment?.Reactions?.find(
            (reaction) => reaction._id === currentUser._id
          );
          setReactions(reactions);
        });
        const reactions = {
          _id: currentUser._id,
          ReactionType: voteType
        };
        if (comment._id === topicData.BestSolution) {
          useForumStore
            .getState()
            .setBestSolutionReactions(
              topicId,
              reactions,
              commentVoteCount,
              comment._id
            );
        } else {
          useForumStore
            .getState()
            .updateCommentVote(
              topicId,
              comment._id,
              reactions,
              commentVoteCount
            );
        }
      }
      if (comment._id !== topicData.BestSolution)
        setVoteCount(commentVoteCount);
    }
  }

  return (
    <div className={styles.repliesDrawer}>
      <div className="h6-600">{comment._created_by.Name}</div>
      <div className={`small ${styles.shortTime}`}>
        <Time
          date={comment._created_at}
          type={TIME_FORMAT_TYPES.RELATIVE_SHORT}
        />
      </div>
      {!isNoContent(commentContent) && (
        <ReadonlyEditor
          value={commentContent}
          renderer={plugin}
          className={`${styles.repliesEditor} ${Classes.baseplus400}`}
        />
      )}
      <CommentAttachments attachments={attachments} readonly={true} />
      <div className={`${styles.df8} ${styles.mt12}`}>
        <Tooltip
          content={<Trans>Upvote</Trans>}
          type={TOOLTIP_TYPES.SMALL}
          position={TOOLTIP_POSITION.TOP}
        >
          <Icon
            paddingNone={true}
            highlight={false}
            onClick={() => postVote(UPVOTE)}
          >
            {isUpvoted ? (
              <UpvotedIcon />
            ) : (
              <UpvoteIcon
                className={`${styles.voteIcon} ${
                  isDisableVote ? styles.disableIcon : styles.up
                }`}
              />
            )}
          </Icon>
        </Tooltip>
        {voteCount}
        <Tooltip
          content={<Trans>Downvote</Trans>}
          type={TOOLTIP_TYPES.SMALL}
          position={TOOLTIP_POSITION.TOP}
        >
          <Icon
            paddingNone={true}
            highlight={false}
            onClick={() => postVote(DOWNVOTE)}
          >
            {isDownvoted ? (
              <DownvotedIcon />
            ) : (
              <DownvoteIcon
                className={`${styles.voteIcon} ${
                  isDisableVote ? styles.disableIcon : styles.down
                }`}
              />
            )}
          </Icon>
        </Tooltip>
      </div>
    </div>
  );
}

Comment.propTypes = {
  comment: PropTypes.object,
  teamId: PropTypes.string,
  topicId: PropTypes.string
};
